import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import {
  getSupplyChainDelayedPoQueue,
  type OverdueOrDueSoonPORow,
  type PoAgingBucket,
  type SupplyChainDashboardActionSummary,
} from '@/services/supplyChainDashboard.service';

const { Text } = Typography;

const agingColor: Record<PoAgingBucket, string> = {
  '0-7': 'green',
  '8-14': 'gold',
  '15-30': 'orange',
  '30+': 'red',
};

export const OverduePoFollowUpZone: React.FC<{
  summary: SupplyChainDashboardActionSummary;
  asOnDate?: string;
  formatCurrency: (v: number) => string;
  initialMode?: 'overdue' | 'due_soon';
}> = ({ summary, asOnDate, formatCurrency, initialMode = 'overdue' }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'overdue' | 'due_soon'>(initialMode);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [aging, setAging] = useState<PoAgingBucket | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    setMode(initialMode);
    setPage(1);
  }, [initialMode]);

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'scm-delayed-po-queue',
      mode,
      page,
      pageSize,
      aging,
      searchApplied,
      asOnDate,
    ],
    queryFn: () =>
      getSupplyChainDelayedPoQueue({
        mode,
        page,
        pageSize,
        aging_bucket: mode === 'overdue' ? aging : undefined,
        search: searchApplied || undefined,
        as_on_date: asOnDate,
      }),
    placeholderData: (prev) => prev,
  });

  const overdueCount = summary.overdue_po_count ?? 0;
  const dueSoonCount = summary.due_soon_po_count ?? 0;
  const agingCounts = data?.aging ?? summary.overdue_aging;
  const total = data?.total ?? (mode === 'overdue' ? overdueCount : dueSoonCount);
  const totalValue =
    data?.total_value ??
    (mode === 'overdue' ? summary.overdue_po_value : summary.due_soon_po_value);
  const rows = data?.rows ?? [];

  const columns: ColumnsType<OverdueOrDueSoonPORow> = [
    {
      title: 'PO #',
      dataIndex: 'po_number',
      key: 'po_number',
      width: 140,
      ellipsis: true,
      render: (v: string, r) => (
        <a onClick={() => navigate(r.link_path || `/scm/procurement/purchase-orders/${r.id}`)}>{v}</a>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
      ellipsis: true,
      render: (v: string, r) =>
        r.supplier_id || r.supplier_link_path ? (
          <a
            onClick={() =>
              navigate(
                r.supplier_link_path || `/scm/procurement/suppliers/${r.supplier_id}`
              )
            }
          >
            {v || '—'}
          </a>
        ) : (
          v || '—'
        ),
    },
    {
      title: 'Delivery',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 110,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    ...(mode === 'overdue'
      ? ([
          {
            title: 'Overdue',
            dataIndex: 'overdue_days',
            key: 'overdue_days',
            width: 120,
            sorter: (a: OverdueOrDueSoonPORow, b: OverdueOrDueSoonPORow) =>
              (a.overdue_days ?? 0) - (b.overdue_days ?? 0),
            defaultSortOrder: 'descend' as const,
            render: (d: number | undefined, r: OverdueOrDueSoonPORow) => {
              const bucket = r.aging_bucket ?? '0-7';
              return (
                <Tag color={agingColor[bucket]} style={{ marginInlineEnd: 0 }}>
                  {d ?? 0}d · {bucket}
                </Tag>
              );
            },
          },
        ] as ColumnsType<OverdueOrDueSoonPORow>)
      : []),
    {
      title: 'Status',
      dataIndex: 'po_status',
      key: 'po_status',
      width: 120,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer',
      key: 'buyer',
      width: 120,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Amount',
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      width: 120,
      render: (v: number) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(v ?? 0)}</span>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button
          type="link"
          size="small"
          onClick={() => navigate(r.link_path || `/scm/procurement/purchase-orders/${r.id}`)}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <ControlTowerSectionCard
      zone="overdue"
      sectionId="overdue-pos"
      title={
        <Space>
          <WarningOutlined />
          <span>
            Delayed PO follow-up ({overdueCount} overdue · {dueSoonCount} due soon)
          </span>
        </Space>
      }
      extra={
        <Button type="link" size="small" onClick={() => navigate('/scm/procurement/purchase-orders')}>
          PO register
        </Button>
      }
    >
      <ControlTowerMetricStrip zone="metricsPayables" title="Delivery risk">
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title="Overdue POs"
            count={overdueCount}
            amount={summary.overdue_po_value}
            formatCurrency={formatCurrency}
            icon={<WarningOutlined />}
            urgency={overdueCount > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: 'Follow up',
              onClick: () => {
                setMode('overdue');
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title="Due soon (7d)"
            count={dueSoonCount}
            amount={summary.due_soon_po_value}
            formatCurrency={formatCurrency}
            icon={<ClockCircleOutlined />}
            urgency={dueSoonCount > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'View queue',
              onClick: () => {
                setMode('due_soon');
                setAging(undefined);
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title="Aging 30+"
            count={agingCounts?.['30+'] ?? 0}
            urgency={(agingCounts?.['30+'] ?? 0) > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: 'Filter',
              onClick: () => {
                setMode('overdue');
                setAging('30+');
                setPage(1);
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <ControlTowerMetricTile
            title="Aging 15–30"
            count={agingCounts?.['15-30'] ?? 0}
            urgency={(agingCounts?.['15-30'] ?? 0) > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Filter',
              onClick: () => {
                setMode('overdue');
                setAging('15-30');
                setPage(1);
              },
            }}
          />
        </Col>
      </ControlTowerMetricStrip>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12, marginBottom: 12 }}
        message="Full open-PO delivery queue (not capped). Chase suppliers on overdue lines; confirm due-soon dates before they slip."
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Search PO # / supplier / buyer"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => {
            setSearchApplied(v.trim());
            setPage(1);
          }}
        />
        {mode === 'overdue' ? (
          <Select
            allowClear
            placeholder="Aging"
            style={{ width: 130 }}
            value={aging}
            onChange={(v) => {
              setAging(v);
              setPage(1);
            }}
            options={[
              { value: '0-7', label: `0–7 (${agingCounts?.['0-7'] ?? 0})` },
              { value: '8-14', label: `8–14 (${agingCounts?.['8-14'] ?? 0})` },
              { value: '15-30', label: `15–30 (${agingCounts?.['15-30'] ?? 0})` },
              { value: '30+', label: `30+ (${agingCounts?.['30+'] ?? 0})` },
            ]}
          />
        ) : null}
        <Button size="small" onClick={() => refetch()} loading={isFetching}>
          Refresh queue
        </Button>
        {totalValue > 0 ? (
          <Text type="secondary">
            Queue value: {formatCurrency(totalValue)} · {total} PO{total === 1 ? '' : 's'}
          </Text>
        ) : null}
      </Space>

      <Tabs
        size="small"
        activeKey={mode}
        onChange={(k) => {
          setMode(k as 'overdue' | 'due_soon');
          setPage(1);
          if (k === 'due_soon') setAging(undefined);
        }}
        items={[
          {
            key: 'overdue',
            label: `Overdue (${overdueCount})`,
            children:
              mode !== 'overdue' ? null : isError ? (
                <Alert
                  type="error"
                  showIcon
                  message={(error as Error)?.message ?? 'Failed to load delayed PO queue'}
                />
              ) : isFetching && !data ? (
                <Spin />
              ) : rows.length === 0 ? (
                <EmptyQueueState message="No overdue open POs for the selected filters." />
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  tableLayout="fixed"
                  loading={isFetching}
                  dataSource={rows}
                  columns={columns}
                  scroll={{ x: 1100 }}
                  pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
                    onChange: (nextPage, nextSize) => {
                      setPage(nextPage);
                      if (nextSize && nextSize !== pageSize) {
                        setPageSize(nextSize);
                        setPage(1);
                      }
                    },
                  }}
                />
              ),
          },
          {
            key: 'due_soon',
            label: `Due soon (${dueSoonCount})`,
            children:
              mode !== 'due_soon' ? null : isError ? (
                <Alert
                  type="error"
                  showIcon
                  message={(error as Error)?.message ?? 'Failed to load delayed PO queue'}
                />
              ) : isFetching && !data ? (
                <Spin />
              ) : rows.length === 0 ? (
                <EmptyQueueState message="No open POs due in the next 7 days." />
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  tableLayout="fixed"
                  loading={isFetching}
                  dataSource={rows}
                  columns={columns}
                  scroll={{ x: 1100 }}
                  pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
                    onChange: (nextPage, nextSize) => {
                      setPage(nextPage);
                      if (nextSize && nextSize !== pageSize) {
                        setPageSize(nextSize);
                        setPage(1);
                      }
                    },
                  }}
                />
              ),
          },
        ]}
      />
    </ControlTowerSectionCard>
  );
};

export default OverduePoFollowUpZone;
